import { useQuery } from '@tanstack/react-query';
import { Descriptions, Divider, Flex, Grid, Skeleton, Table, Tabs, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { Fragment, useContext, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { getManpowerSupplyTicket } from 'src/apis/helpdesk/manpowerSupplyTicket.api';
import { getBusinessTripTicket } from 'src/apis/hr_ticket/businessTripTicket.api';
import { BtnHrHandle } from 'src/components/v2/Button';
import { HRTicketStatusTag } from 'src/components/v2/Tag/TicketStatusTag/TicketStatusTag';
import { ConfirmTypeEn } from 'src/constants/confirmTypeEn.enum';
import urlPath from 'src/constants/urlPath';
import { AppContext } from 'src/contexts/app.context';
import { HrTicketDetailContext } from 'src/contexts/hrTicketDetail.context';
import { TicketContext } from 'src/contexts/ticket.context';
import useCalcTimeBusiness from 'src/hooks/useCalcTimeBusiness';
import type { IManpowerSupplyForm } from 'src/types/helpdesk/manpowerSupplyForm.type';
import type { IBusinessTripTicketDetail } from 'src/types/hr_ticket/businessTripTicket.type';
import type { ProjectAdd } from 'src/types/hr_ticket/overtimeTicket.type';
import { formatDate, formatTimeString, totalOfRangeDate } from 'src/utils/utils';
import HrConfirmBusinessTripTicket from '../ConfirmBusinessTripTicket/HrConfirmBusinessTripTicket';
import ManpowerSupplyTab from '../components/ManpowerSupplyTab/ManpowerSupplyTab';
import './businessTripTicketDetail.scss';
const { Title, Text } = Typography;

export type OpenType = {
  isOpen: boolean;
};

const columns = function (
  calcTotalTime: (
    startTimeInput: string | undefined,
    endTimeInput: string | undefined,
    employeeId: number | undefined
  ) => number
): ColumnsType<Partial<IBusinessTripTicketDetail>> {
  const columnTitle: ColumnsType<Partial<IBusinessTripTicketDetail>> = [
    {
      title: 'Người đăng ký',
      dataIndex: 'employee',
      key: 'employee',
      align: 'center',
      width: '20%',
      render: (_, record) => (
        <Text strong>
          {record.employee?.code} - {record.employee?.fullName}
        </Text>
      )
    },
    {
      title: 'Dự án thực hiện',
      dataIndex: 'projectCode',
      key: 'projectCode',
      align: 'center',
      width: '20%',
      render: (_, record) => <Text>{(record.projects as ProjectAdd[])?.map((x) => x.projectCode)?.join(', ')}</Text>
    },
    {
      title: 'Ngày công tác',
      dataIndex: 'date',
      key: 'date',
      align: 'center',
      width: '20%',
      render: (_, record) => {
        return (
          <Flex vertical gap={5}>
            <Text>
              {formatDate(record?.fromDate)} - {formatDate(record?.toDate)}
            </Text>
            <Text strong>Tổng: {totalOfRangeDate(record?.fromDate, record?.toDate)} Ngày</Text>
          </Flex>
        );
      }
    },
    {
      title: 'Thời gian',
      dataIndex: '',
      key: 'time',
      align: 'center',
      width: '10%',
      render: (_, record) => (
        <Flex vertical gap={5}>
          <Text>
            {formatTimeString(record?.startTime, true)} - {formatTimeString(record?.endTime)}
          </Text>
          <Text strong>Tổng: {calcTotalTime(record?.startTime, record?.endTime, record.employeeId)} Tiếng</Text>
        </Flex>
      )
    },
    {
      title: 'Số ngày lưu trú',
      dataIndex: '',
      key: 'time',
      width: '15%',
      align: 'center',
      render: (_, record) => <Text>{record.numDaysOfStay} Ngày</Text>
    },
    {
      title: 'Số ngày công tác phí',
      dataIndex: '',
      key: 'time',
      width: '15%',
      align: 'center',
      render: (_, record) => <Text>{record.numDaysOfBusinessTrip} Ngày</Text>
    }
  ];
  return columnTitle;
};

export type OpenConfirmType = {
  id?: number;
  isOpen: boolean;
  confirms?: IBusinessTripTicketDetail[];
  manpowerSupply?: IManpowerSupplyForm;
};

const { useBreakpoint } = Grid;

export default function BusinessTripTicketDetail() {
  const { setInvalidKey } = useContext(TicketContext);
  const { ticketSelected } = useContext(AppContext);
  const { setBusinessTripTicket, setLoading } = useContext(HrTicketDetailContext);
  const [openHrConfirm, setOpenHrConfirm] = useState<OpenConfirmType>({ isOpen: false, confirms: [] });
  const { calcTotalTime } = useCalcTimeBusiness();
  const { id } = useParams();
  const [tab, setTab] = useState('1');
  const breakpoints = useBreakpoint();

  const colspan = breakpoints.xxl ? 2 : 1;

  const businessTripTicketQuery = useQuery({
    queryKey: ['businessTripTicket', id],
    queryFn: () => getBusinessTripTicket(Number(id)),
    enabled: !!id && !!ticketSelected
  });

  const manpowerSupplyQuery = useQuery({
    queryKey: ['manpowerSupplyTicket', Number(businessTripTicketQuery?.data?.data.manpowerSupplyId)],
    queryFn: () => getManpowerSupplyTicket(Number(businessTripTicketQuery?.data?.data.manpowerSupplyId)),
    enabled: !!Number(businessTripTicketQuery?.data?.data.manpowerSupplyId),
    staleTime: 60 * 1000
  });

  useEffect(() => {
    if (businessTripTicketQuery.data) {
      setBusinessTripTicket(businessTripTicketQuery.data);
      setLoading(businessTripTicketQuery.isFetching);
      setInvalidKey(JSON.stringify(businessTripTicketQuery.data?.data));
    }
  }, [
    businessTripTicketQuery.data,
    businessTripTicketQuery.isFetching,
    setBusinessTripTicket,
    setInvalidKey,
    setLoading
  ]);

  const tabItems = [
    {
      label: 'Phiếu công tác',
      key: '1',
      children: (
        <Fragment>
          <Flex justify='start'>
            <Title level={5}>Thông tin phiếu</Title>
          </Flex>
          {businessTripTicketQuery.isFetching ? (
            <Skeleton />
          ) : (
            <Fragment>
              <Descriptions
                className='business-trip-item-header'
                layout='horizontal'
                labelStyle={{ width: '135px' }}
                column={{ xs: 1, sm: 1, md: 1, lg: 1, xl: 1, xxl: 2 }}
              >
                <Descriptions.Item label='Mã phiếu'>
                  <Text strong>{businessTripTicketQuery.data?.data?.code}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Trạng thái'>
                  <HRTicketStatusTag
                    ticketStatus={businessTripTicketQuery.data?.data.ticketStatus}
                    erpKey={businessTripTicketQuery.data?.data.erpKey}
                    isConfirm={businessTripTicketQuery.data?.data.isConfirm}
                    hrConfirmedType={businessTripTicketQuery.data?.data.hrConfirmedType}
                  />
                </Descriptions.Item>
                <Descriptions.Item label='Ngày tạo' span={colspan}>
                  <Text>{formatDate(businessTripTicketQuery.data?.data?.createdDate)}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Người tạo'>
                  <Text>{businessTripTicketQuery.data?.data?.createdBy?.fullName}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Bộ phận'>
                  <Text>{businessTripTicketQuery.data?.data?.createdBy?.department?.name}</Text>
                </Descriptions.Item>
                <Descriptions.Item label='Ticket raise' span={colspan}>
                  <Text strong>
                    <Link to={`${urlPath.myTicket.location}/${businessTripTicketQuery.data?.data?.refTicketIdLegacy}`}>
                      {businessTripTicketQuery.data?.data?.refTicketCode}
                    </Link>
                  </Text>
                </Descriptions.Item>
                <Descriptions.Item label='Mã dự án' span={colspan}>
                  {businessTripTicketQuery.data?.data?.projectCode}
                </Descriptions.Item>
                <Descriptions.Item label='Số phiếu ERP' span={colspan}>
                  {businessTripTicketQuery.data?.data?.erpKey}
                </Descriptions.Item>
                <Descriptions.Item label='Loại công tác' span={colspan}>
                  {businessTripTicketQuery.data?.data?.isAbroad ? 'Nước ngoài' : 'Trong nước'}
                </Descriptions.Item>
                <Descriptions.Item label='Nơi đi' span={colspan}>
                  {businessTripTicketQuery.data?.data?.departureIdInString || ''}
                </Descriptions.Item>
                {!businessTripTicketQuery.data?.data.isAbroad ? (
                  <Descriptions.Item label='Nơi đến' span={colspan}>
                    {businessTripTicketQuery.data?.data?.destinationIdInString}
                  </Descriptions.Item>
                ) : (
                  <Descriptions.Item label='Nơi đến' span={colspan}>
                    {manpowerSupplyQuery.data?.data?.workplaceAddress &&
                      manpowerSupplyQuery.data?.data?.workplaceAddress + ', '}
                    {manpowerSupplyQuery.data?.data.national}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label={'Chi tiết công việc'} span={colspan}>
                  <Text strong italic>
                    {businessTripTicketQuery.data?.data?.note || ''}
                  </Text>
                </Descriptions.Item>
              </Descriptions>
              <Divider />
              <Flex vertical>
                <Flex vertical>
                  <Flex justify='space-between' align='center' className='overtime-detail-list-heading'>
                    <Title level={5} style={{ marginBottom: '0px' }}>
                      Danh sách đăng ký
                    </Title>
                  </Flex>
                  <Table
                    className='business-trip-confirm-table'
                    columns={columns(calcTotalTime)}
                    rowKey={(record) => String(record.id)}
                    dataSource={businessTripTicketQuery.data?.data.businessTripTicketDetails}
                    pagination={false}
                    bordered={false}
                    size='middle'
                  />
                </Flex>
                {businessTripTicketQuery.data?.data.isConfirm === ConfirmTypeEn.Confirmed && <Divider />}
                {businessTripTicketQuery.data?.data.isConfirm === ConfirmTypeEn.Confirmed && (
                  <Flex vertical>
                    <Flex justify='space-between' align='center' className='overtime-detail-list-heading'>
                      <Title level={5} style={{ marginBottom: '0px' }}>
                        Danh sách xác nhận
                      </Title>
                    </Flex>
                    <Table
                      className='business-trip-confirm-table'
                      rowClassName={'confirmed'}
                      columns={columns(calcTotalTime)}
                      rowKey={(record) => String(record.id)}
                      dataSource={businessTripTicketQuery.data?.data.confirms}
                      pagination={false}
                      bordered={false}
                      size='middle'
                    />
                  </Flex>
                )}
              </Flex>
            </Fragment>
          )}
        </Fragment>
      )
    },
    {
      label: 'Phiếu cung ứng nhân lực',
      key: '2',
      children: (
        <Fragment>
          {manpowerSupplyQuery.isFetching ? (
            <Skeleton />
          ) : (
            <ManpowerSupplyTab manpowerSupply={manpowerSupplyQuery.data?.data} />
          )}
        </Fragment>
      )
    }
  ];

  return (
    <div className='business-trip-ticket-detail-wrapper'>
      <BtnHrHandle
        type='businessTripTicket'
        onClick={() =>
          setOpenHrConfirm({
            id: Number(id),
            isOpen: true,
            confirms: businessTripTicketQuery.data?.data.confirms,
            manpowerSupply: manpowerSupplyQuery.data?.data
          })
        }
      />
      <Divider style={{ marginTop: 16, marginBottom: 16 }} />
      <Tabs activeKey={tab} onChange={(value) => setTab(value)} type='card' size='middle' items={tabItems} />

      {openHrConfirm && <HrConfirmBusinessTripTicket open={openHrConfirm} setOpen={setOpenHrConfirm} />}
    </div>
  );
}
